/**
 * Active portfolios: the top-left selection of 1..N portfolios, or "all portfolios".
 *
 * A selection is { ids, all, target }:
 * - ids: the selected portfolio ids, in name order. Never empty while the account has portfolios.
 * - all: "Kaikki salkut". Dynamic: a portfolio created later joins on the next reconcile.
 * - target: the one portfolio that new records go to and that single-portfolio views show.
 *   Always one of ids. It is `state.portfolio.portfolio` in the store, so the forms work unchanged.
 *
 * Everything here is pure so it can be tested without the store. (ERP #1409)
 */

// Kept in the browser per user and account (decision Q460), and kept over logout on purpose
// (UX criterion 17), so it is not in services/sessionStorage SESSION_KEYS. Holds only numeric
// ids and flags, never names.
export const STORAGE_KEY = "investime.activePortfolios.v1";

// Value of the "Kaikki salkut" option in the selector.
export const ALL_PORTFOLIOS = "all";

const isId = (value) => Number.isInteger(value) && value > 0;

const uniqueIds = (ids) => [...new Set((ids || []).filter(isId))];

export const sortPortfolios = (portfolios) =>
  [...(portfolios || [])].sort((a, b) => {
    const byName = String(a?.name || "").localeCompare(String(b?.name || ""), "fi", { sensitivity: "base" });
    return byName !== 0 ? byName : (a?.id || 0) - (b?.id || 0);
  });

const sortedIds = (portfolios) => sortPortfolios(portfolios).map((p) => p.id);

/**
 * Decide the selection against the portfolios the server returned.
 *
 * `saved` is the selection to keep if it is still valid (the stored one at startup, the current
 * one on a later list refresh). Ids that are gone are dropped and `pruned` is true. Only if
 * nothing valid is left does `fallbackId` (myUser.active_portfolio) apply, then the first portfolio.
 */
export const reconcileSelection = (portfolios, saved, fallbackId) => {
  const list = portfolios || [];
  if (list.length === 0) {
    return { ids: [], all: false, target: null, pruned: false };
  }
  const allIds = sortedIds(list);
  let pruned = false;

  if (saved && saved.all) {
    return {
      ids: allIds,
      all: true,
      target: allIds.includes(saved.target) ? saved.target : allIds[0],
      pruned: false,
    };
  }

  if (saved) {
    const savedIds = uniqueIds(saved.ids);
    const kept = allIds.filter((id) => savedIds.includes(id));
    pruned = kept.length < savedIds.length;
    if (kept.length > 0) {
      return {
        ids: kept,
        all: false,
        target: kept.includes(saved.target) ? saved.target : kept[0],
        pruned,
      };
    }
  }

  // The server's order, as getPortfolios used to pick data[0].
  const fallback = allIds.includes(fallbackId) ? fallbackId : list[0].id;
  return { ids: [fallback], all: false, target: fallback, pruned };
};

/**
 * Toggle one option of the selector. The last selected portfolio cannot be removed:
 * the selection stays and `blocked` is true, so the selector can say why.
 * Unselecting "all" goes back to the target alone.
 */
export const toggleSelection = (selection, optionId, portfolios) => {
  const allIds = sortedIds(portfolios);
  const all = Boolean(selection?.all);
  const current = all ? allIds : allIds.filter((id) => (selection?.ids || []).includes(id));
  const target = current.includes(selection?.target) ? selection.target : current[0] ?? null;
  const unchanged = { ids: current, all, target, blocked: false };

  if (optionId === ALL_PORTFOLIOS) {
    if (all) {
      const keep = target ?? allIds[0];
      return { ids: keep ? [keep] : [], all: false, target: keep ?? null, blocked: false };
    }
    return { ids: allIds, all: true, target: target ?? allIds[0] ?? null, blocked: false };
  }

  if (current.includes(optionId)) {
    if (current.length <= 1) {
      return { ...unchanged, blocked: true };
    }
    const ids = current.filter((id) => id !== optionId);
    return { ids, all: false, target: ids.includes(target) ? target : ids[0], blocked: false };
  }

  if (!allIds.includes(optionId)) {
    return unchanged;
  }
  const ids = allIds.filter((id) => id === optionId || current.includes(id));
  return { ids, all: false, target: target ?? optionId, blocked: false };
};

/** Closed selector text: "Nimi (VAL)", "{n} salkkua" or "Kaikki salkut (N)". */
export const selectionSummary = ({ active, all, total }, t) => {
  const list = active || [];
  if (all) {
    return t("active_portfolios_all", "Kaikki salkut ({{count}})", { count: total ?? list.length });
  }
  if (list.length === 1) {
    return `${list[0].name} (${list[0].currency_label})`;
  }
  if (list.length === 0) {
    return "";
  }
  return t("active_portfolios_count", "{{count}} salkkua", { count: list.length });
};

/**
 * Company line under the selector (business accounts). Returns { text, title } or null when
 * none of the active portfolios has a company. `title` lists every company.
 */
export const companiesLine = (active, companies, t) => {
  const names = [];
  (active || []).forEach((portfolio) => {
    const company = (companies || []).find((c) => c?.id === portfolio?.company);
    if (company?.name && !names.includes(company.name)) {
      names.push(company.name);
    }
  });
  if (names.length === 0) {
    return null;
  }
  const title = names.join(", ");
  const [a, b] = names;
  let text;
  if (names.length === 1) {
    text = t("role_companies_one", "Yritys: {{name}}", { name: a });
  } else if (names.length === 2) {
    text = t("role_companies_two", "Yritykset: {{a}}, {{b}}", { a, b });
  } else if (names.length === 3) {
    text = t("role_companies_more_one", "Yritykset: {{a}}, {{b}} ja 1 muu", { a, b });
  } else {
    text = t("role_companies_more", "Yritykset: {{a}}, {{b}} ja {{count}} muuta", { a, b, count: names.length - 2 });
  }
  return { text, title };
};

/** "<userId>:<accountId>", or null if either is missing (then nothing is stored). */
export const storageKey = (userId, accountId) =>
  isId(userId) && isId(accountId) ? `${userId}:${accountId}` : null;

const getStorage = (storage) => {
  if (storage) {
    return storage;
  }
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    // Blocked storage: the selection just is not kept.
    return null;
  }
};

const readMap = (storage) => {
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
};

const sanitize = (selection) => {
  const ids = uniqueIds(selection?.ids);
  return {
    ids,
    all: Boolean(selection?.all),
    target: isId(selection?.target) ? selection.target : null,
  };
};

/** The stored selection of this user and account, or null. */
export const readStored = (key, storage) => {
  const store = getStorage(storage);
  if (!key || !store) {
    return null;
  }
  const entry = readMap(store)[key];
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const selection = sanitize(entry);
  return selection.all || selection.ids.length > 0 ? selection : null;
};

/** Store the selection of this user and account; other entries are kept. */
export const writeStored = (key, selection, storage) => {
  const store = getStorage(storage);
  if (!key || !store) {
    return;
  }
  try {
    const map = readMap(store);
    map[key] = sanitize(selection);
    store.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    // Quota or blocked storage: the selection just is not kept.
  }
};

const DEFAULT_REPORT_CURRENCIES = ["EUR", "USD", "SEK"];

/**
 * Reporting currency of a set of portfolios, as the server decides it: the common currency,
 * or EUR when they differ. `currencyOptions` is what the report's currency select offers.
 */
export const reportCurrencyInfo = (portfolios) => {
  const currencies = [];
  (portfolios || []).forEach((p) => {
    if (p?.currency_label && !currencies.includes(p.currency_label)) {
      currencies.push(p.currency_label);
    }
  });
  const mixedCurrency = currencies.length > 1;
  return {
    currency: mixedCurrency ? "EUR" : currencies[0] || null,
    mixedCurrency,
    currencies,
    currencyOptions: [...new Set([...DEFAULT_REPORT_CURRENCIES, ...currencies])],
  };
};

/**
 * Initial SALKKU filter of a report from the active portfolios, in the shape the report views
 * already use: `portfolio` ids and `selected_portfolios` options ({ id, value, name }).
 */
export const reportPortfolioInit = (active) => {
  const list = active || [];
  return {
    portfolio: list.map((p) => p.id),
    selected_portfolios: list.map((p) => ({ id: p.id, value: p.name, name: p.name })),
    ...reportCurrencyInfo(list),
  };
};

/**
 * The portfolios a report's own SALKKU filter has selected, from its options ({ id }) or ids,
 * for reportCurrencyInfo. Unknown ids are left out.
 */
export const reportSelectedPortfolios = (selected, portfolios) =>
  (selected || [])
    .map((item) => (item && typeof item === "object" ? item.id : item))
    .map((id) => (portfolios || []).find((p) => p?.id === Number(id)))
    .filter(Boolean);

/**
 * Fetch a list from an endpoint that takes one portfolio, for each active portfolio, and merge
 * the results in portfolio order. With one active portfolio it is one request with
 * `portfolio: id` and its data as is, as before.
 */
export const fetchForEachPortfolio = (active, fetcher) => {
  const ids = (active || []).length > 0 ? active.map((p) => p.id) : [undefined];
  return Promise.all(ids.map((id) => fetcher({ portfolio: id }).then((response) => response?.data)))
    .then((lists) => {
      if (lists.length === 1) {
        return lists[0];
      }
      return [].concat(...lists.map((list) => (Array.isArray(list) ? list : [])));
    });
};

/**
 * List request params for the active portfolios. One portfolio is sent as `portfolio=<id>` as
 * before; several as an array, which serializeParams sends as `portfolio[]=..`.
 */
export const listPortfolioParams = (active) => {
  const ids = (active || []).map((p) => p.id);
  if (ids.length === 0) {
    return {};
  }
  return { portfolio: ids.length === 1 ? ids[0] : ids };
};
